import { apiClient } from '@/api/client';
import {
  Business,
  BusinessMapReferenceInfoDTO,
  CachedMapResponseDTO,
  MapProviderInfoDTO,
  RefreshComparisonDTO,
  UpdateMapReferencePreviewDTO,
} from '@/api/models';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type CachedMapResponse = CachedMapResponseDTO;
export type RefreshComparison = RefreshComparisonDTO;
export type MapProviderInfo = MapProviderInfoDTO;
export type BusinessMapReferenceInfo = BusinessMapReferenceInfoDTO;
export type UpdateMapReferencePreview = UpdateMapReferencePreviewDTO;

export const useMapReferenceInfo = (businessId: number | undefined) => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_BUSINESS, businessId, 'map-reference'],
    queryFn: async () => {
      if (!businessId) return null;
      const response = await apiClient.GET(
        '/admin/import/businesses/{id}/map-reference',
        {
          params: { path: { id: businessId } },
        },
      );
      if (response.error) return null;
      return response.data as BusinessMapReferenceInfo | null;
    },
    enabled: !!businessId,
  });
};

export const usePreviewMapReferenceUpdate = () => {
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: async ({
      businessId,
      newPlaceId,
    }: {
      businessId: number;
      newPlaceId: string;
    }) => {
      const response = await apiClient.GET(
        '/admin/import/businesses/{id}/map-reference/preview',
        {
          params: {
            path: { id: businessId },
            query: { newPlaceId },
          },
        },
      );
      if (response.error) throw new Error('Failed to preview new place ID');
      return response.data as UpdateMapReferencePreview;
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to preview new place ID',
      });
    },
  });
};

export const useUpdateMapReference = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      businessId,
      newPlaceId,
    }: {
      businessId: number;
      newPlaceId: string;
    }) => {
      const response = await apiClient.PUT(
        '/admin/import/businesses/{id}/map-reference',
        {
          params: { path: { id: businessId } },
          body: { newPlaceId },
        },
      );
      if (response.error) throw new Error('Failed to update place ID');
      return {
        business: response.data as Business,
        businessId,
      };
    },
    onSuccess: result => {
      addNotification({
        type: 'success',
        title: 'Place ID Updated',
        message:
          'The map provider place ID has been updated. You can now sync the business data.',
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_BUSINESS, String(result.businessId)],
      });
      queryClient.invalidateQueries({
        queryKey: [
          queryKeys.ADMIN_BUSINESS,
          result.businessId,
          'map-reference',
        ],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to update place ID',
      });
    },
  });
};

export const useFetchRefreshPreview = () => {
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: async (businessId: number) => {
      const response = await apiClient.GET(
        '/admin/import/businesses/{id}/refresh-preview',
        {
          params: { path: { id: businessId } },
        },
      );
      if (response.error) throw new Error('Failed to fetch refresh preview');
      return response.data as RefreshComparison;
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to check for updates',
      });
    },
  });
};

export const useApplyRefresh = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      businessId: number;
      providerName?: string;
      applyName?: boolean;
      applyAddress?: boolean;
      applyPhone?: boolean;
      applyWebsite?: boolean;
      applyOpeningHours?: boolean;
      addNewPhotos?: boolean;
      cachedResponse?: CachedMapResponse;
    }) => {
      const response = await apiClient.POST(
        '/admin/import/businesses/{id}/refresh',
        {
          params: { path: { id: data.businessId } },
          body: {
            applyName: data.applyName ?? false,
            applyAddress: data.applyAddress ?? false,
            applyPhone: data.applyPhone ?? false,
            applyWebsite: data.applyWebsite ?? false,
            applyOpeningHours: data.applyOpeningHours ?? false,
            addNewPhotos: data.addNewPhotos ?? false,
            cachedResponse: data.cachedResponse,
          },
        },
      );
      if (response.error) throw new Error('Failed to apply refresh');
      return {
        business: response.data as Business,
        businessId: data.businessId,
        providerName: data.providerName || 'Map Provider',
      };
    },
    onSuccess: result => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: `Business data refreshed from ${result.providerName}`,
      });
      // Invalidate the list of businesses
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
      // Invalidate all business queries (covers any business detail views)
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESS] });
      // Also invalidate with specific business ID (as string since URL params are strings)
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_BUSINESS, String(result.businessId)],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to refresh business data',
      });
    },
  });
};
